import { ComponentObservable, componentObserver } from '../../component-observer';
import { currentBreakpoint } from '../../utils/resize.utils';

/**
 * Initializes the country selector.
 * Extends the slider module to add swipe on mobile and tablet.
 */
class ProductDetailModule implements ComponentObservable {
  /**
   * Selector of all elements that must be initialized in javascript.
   */
  componentSelector = '.section--product-detail';

  /**
   * Current breakpoint
   */
  breakpoint = null;

  /**
   * Initialize all product details on the current page.
   * @param observe Boolean if the ProductDetailModule should listen for changes in the DOM and initialize dynamically
   *   added elements
   */
  initializeAll(observe: boolean): void {
    const allComponents = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allComponents.length; i++) {
      this.initialize(allComponents[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize elements when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Positions the heading on the top or next to the image depending on screen size
   * @param root The product detail root element
   */
  positionHeadline(root: HTMLElement): void {
    const heading = root.querySelector<HTMLElement>('.product-detail-heading');
    const rightContent = root.querySelector<HTMLElement>('.text-and-image-component__right-content');
    const textAndImageComponent = root.querySelector<HTMLElement>('.text-and-image-component');
    if (rightContent && heading) {
      if (this.breakpoint.isDesktop) {
        rightContent.insertBefore(heading, rightContent.firstElementChild);
      } else {
        textAndImageComponent.parentElement.insertBefore(heading, textAndImageComponent);
      }
    }
  }

  /**
   * Opens the social share on the top of the button on mobile and on the bottom otherwise (default)
   * @param root The product detail root element
   */
  openSocialSharePosition(root: HTMLElement): void {
    const socialShare = root.querySelector<HTMLElement>('.social-share');
    if (socialShare) {
      if (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) {
        socialShare.classList.add('social-share--top');
      } else {
        socialShare.classList.remove('social-share--top');
      }
    }
  }

  /**
   * Initialize the given country selector element.
   * Sets the listener for resize.
   * @param root The product detail root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);
    this.breakpoint = currentBreakpoint();
    this.positionHeadline(root);
    this.openSocialSharePosition(root);

    // timeoutId for debounce mechanism
    let timeoutId = null;
    // Set window resize listener
    const resizeListener = (): void => {
      // Prevent execution of previous setTimeout
      clearTimeout(timeoutId);
      // Change width from the state object after 150 milliseconds
      timeoutId = setTimeout(() => {
        this.breakpoint = currentBreakpoint();
        this.positionHeadline(root);
        this.openSocialSharePosition(root);
      }, 500);
    };
    // Set resize listener
    window.addEventListener('resize', resizeListener);
  }
}

const productDetailModule = new ProductDetailModule();
export { productDetailModule as productDetail };
